import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import CopyrightedItemCard from './CopyrightedItemCard';
import CopyrightReleaseStatusCard from './CopyrightReleaseStatusCard/CopyrightReleaseStatusCard';
import { ActivityType, IPCheckType, QuestionResponse, TechnologyType } from 'store/enums/BallotReviewTypes';
import { TechnologyConflict } from 'store/types/Meeting';
import { defaultSnackbarErrorProps } from 'util/layout';
import { meetingSessionToActivity } from 'util/meeting';
import { useMeetingStaffContext } from 'components/meetings/MeetingPage/MeetingStaffView/MeetingStaffContext';

const inputWidth = 3;

const CopyrightedSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [numberCopyrighted, setNumberCopyrighted] = useState(
    meetingSession?.currentIpCheckSession?.copyrightSummary?.totalConflicts ?? ''
  );
  const [knownCopyright, setKnownCopyright] = useState<QuestionResponse | undefined>(
    meetingSession?.currentIpCheckSession?.copyrightConflict
  );
  const [copyrightedItems, setCopyrightedItems] = useState<TechnologyConflict[]>([]);

  const saveCopyrightProgress = useCallback(
    async (summary: { totalConflicts: string }, copyrightConflict?: QuestionResponse) => {
      try {
        await MeetingService.saveMeetingSession({
          ...meetingSession,
          meetingId,
          currentIpCheckSession: {
            ...meetingSession?.currentIpCheckSession,
            type: IPCheckType.Affirmative,
            copyrightConflict,
            copyrightSummary: summary,
          },
          currentTechnologyConflict: undefined,
        });
      } catch (error) {
        console.error('Error saving copyrighted item summary progress:', error);
      }
    },
    [meetingId, meetingSession]
  );

  const handleSave = useCallback(async () => {
    logActivity(
      {
        ...meetingSessionToActivity(meetingSession),
        type: ActivityType.IpCheck,
        ipCheckSession: {
          type: IPCheckType.Affirmative,
          copyrightConflict: knownCopyright,
          copyrightSummary: {
            totalConflicts: numberCopyrighted,
          },
        },
      },
      'Saved total numbers of copyrighted items'
    );
  }, [knownCopyright, logActivity, meetingSession, numberCopyrighted]);

  const addItem = useCallback(
    () =>
      setCopyrightedItems([
        ...copyrightedItems,
        {
          id: `copyrighted-item-${Date.now()}`,
          type: TechnologyType.Copyright,
          description: '',
        },
      ]),
    [copyrightedItems]
  );

  const updateItem = useCallback((id: string, patch: Partial<TechnologyConflict>) => {
    setCopyrightedItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  const removeItem = useCallback(
    (id: string) => setCopyrightedItems(copyrightedItems.filter((i) => id !== i.id)),
    [copyrightedItems]
  );

  const handleDisplayCopyright = useCallback(
    async (copyright: TechnologyConflict) => {
      try {
        await MeetingService.saveMeetingSession({
          ...meetingSession,
          meetingId,
          currentIpCheckSession: undefined,
          currentTechnologyConflict: copyright,
        });

        enqueueSnackbar(`Displayed copyright`, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Failed to display copyright`, defaultSnackbarErrorProps);
        console.error('Error displaying copyright:', error);
      }
    },
    [enqueueSnackbar, meetingId, meetingSession]
  );

  const handleClearCopyright = useCallback(async () => {
    try {
      await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentTechnologyConflict: undefined,
      });

      enqueueSnackbar(`Cleared copyright`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to clear copyright`, defaultSnackbarErrorProps);
      console.error('Error clearing copyright:', error);
    }
  }, [enqueueSnackbar, meetingId, meetingSession]);

  return (
    <GridContainer>
      <GridItem>
        <Typography variant="h6">Copyrighted Item Subsection</Typography>
      </GridItem>
      <GridItem>
        <GridContainer spacing={1}>
          <GridItem>
            <Typography variant="subtitle2">Total numbers of copyrighted items to be dealt with</Typography>
          </GridItem>
          <GridItem md={inputWidth}>
            <TextField
              label={'Fill Number'}
              value={numberCopyrighted}
              type="number"
              onChange={(event) => {
                const value = event.target.value;
                setNumberCopyrighted(value);
                void saveCopyrightProgress({ totalConflicts: value }, knownCopyright);
              }}
            ></TextField>
          </GridItem>
          <GridItem md={12 - inputWidth} alignContent={'center'}>
            <Typography>
              Known copyrighted items that are used or reproduced to the Standard/Safety Guideline
            </Typography>
          </GridItem>
          <GridItem>
            <RadioGroup
              name="ip-check-type-radio-group"
              value={knownCopyright ?? ''}
              onChange={(event) => {
                const value = event.target.value as QuestionResponse;
                setKnownCopyright(value);
                setCopyrightedItems([]);
                void saveCopyrightProgress({ totalConflicts: numberCopyrighted }, value);
              }}
            >
              <FormControlLabel
                value={QuestionResponse.Yes}
                control={<Radio />}
                label={
                  'There is at least one known copyrighted items that might be relevant to the Standard/Safety Guideline'
                }
              />
              <FormControlLabel
                value={QuestionResponse.No}
                control={<Radio />}
                label={'There is no disclosed copyrighted item'}
              />
            </RadioGroup>
          </GridItem>
        </GridContainer>
      </GridItem>
      <GridItem>
        <Button variant="contained" color="secondary" disabled={!knownCopyright} onClick={handleSave}>
          Save
        </Button>
      </GridItem>
      {knownCopyright === QuestionResponse.Yes && (
        <>
          <GridItem>
            <Typography variant="h6">Assessment of disclosed copyrighted item</Typography>
          </GridItem>
          {copyrightedItems.map((item, index) => (
            <GridItem key={`${item.id}-card`}>
              <CopyrightedItemCard
                item={item}
                index={index}
                updateItem={updateItem}
                removeItem={removeItem}
                displayConflict={handleDisplayCopyright}
                clearConflict={handleClearCopyright}
              />
              {item.isJustified === QuestionResponse.Yes && (
                <CopyrightReleaseStatusCard
                  item={item}
                  index={index}
                  updateItem={updateItem}
                  displayConflict={handleDisplayCopyright}
                />
              )}
            </GridItem>
          ))}
          <GridItem>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={addItem}>
              Add Copyrighted Item
            </Button>
          </GridItem>
        </>
      )}
    </GridContainer>
  );
};

export default CopyrightedSection;
